import React from 'react';
import { motion } from 'framer-motion';
import { 
  GlobeAltIcon, 
  ShieldCheckIcon, 
  CpuChipIcon,
  BoltIcon,
  LockClosedIcon,
  DevicePhoneMobileIcon
} from '@heroicons/react/24/outline';

const UniqueFeature = () => {
  return (
    <div className="relative bg-gradient-to-br from-dark-900 via-dark-800 to-dark-950 py-16 sm:py-24 overflow-hidden">
      {/* Animated Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-grid-pattern opacity-5"></div>
        <motion.div
          className="absolute top-0 left-0 w-96 h-96 bg-green-500/10 rounded-full filter blur-3xl"
          animate={{
            scale: [1, 1.2, 1],
            x: [0, 100, 0],
          }}
          transition={{
            duration: 10,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
        <motion.div
          className="absolute bottom-0 right-0 w-96 h-96 bg-primary-500/10 rounded-full filter blur-3xl"
          animate={{
            scale: [1.2, 1, 1.2],
            x: [0, -100, 0],
          }}
          transition={{
            duration: 12,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
      </div>

      <div className="relative mx-auto max-w-7xl px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center justify-center p-2 bg-green-500/20 rounded-full mb-6">
            <BoltIcon className="h-6 w-6 text-green-400" />
          </div>
          <h2 className="text-4xl font-bold tracking-tight text-white sm:text-5xl mb-4">
            The Only AI That Works
            <span className="block text-green-400 text-5xl font-bold mt-2">
              COMPLETELY OFFLINE
            </span>
          </h2>
          <p className="text-xl text-primary-200 max-w-3xl mx-auto">
            While other AI tools depend on cloud servers and internet connections, Speedr AI processes everything locally on your device.
          </p>
        </motion.div>

        {/* Main Feature Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
            className="space-y-8"
          >
            <div className="bg-dark-800/50 backdrop-blur-sm rounded-2xl p-8 border border-green-500/20">
              <div className="flex items-center mb-6">
                <div className="p-3 rounded-lg bg-red-500/20 border border-red-500/30 mr-4">
                  <GlobeAltIcon className="h-8 w-8 text-red-400" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white">Other AI Tools</h3>
                  <p className="text-red-400 text-sm">Cloud-Dependent</p>
                </div>
              </div>
              <div className="space-y-3">
                <div className="flex items-start">
                  <div className="w-2 h-2 rounded-full bg-red-400 mt-2 mr-3 flex-shrink-0"></div>
                  <p className="text-red-300">Require constant internet connection</p>
                </div>
                <div className="flex items-start">
                  <div className="w-2 h-2 rounded-full bg-red-400 mt-2 mr-3 flex-shrink-0"></div>
                  <p className="text-red-300">Send your data to cloud servers</p>
                </div>
                <div className="flex items-start">
                  <div className="w-2 h-2 rounded-full bg-red-400 mt-2 mr-3 flex-shrink-0"></div>
                  <p className="text-red-300">Privacy concerns & data breaches</p>
                </div>
                <div className="flex items-start">
                  <div className="w-2 h-2 rounded-full bg-red-400 mt-2 mr-3 flex-shrink-0"></div>
                  <p className="text-red-300">Slow response times due to network latency</p>
                </div>
                <div className="flex items-start">
                  <div className="w-2 h-2 rounded-full bg-red-400 mt-2 mr-3 flex-shrink-0"></div>
                  <p className="text-red-300">Monthly subscription fees for cloud access</p>
                </div>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            viewport={{ once: true }}
            className="space-y-8"
          >
            <div className="bg-gradient-to-br from-green-500/20 to-primary-500/20 backdrop-blur-sm rounded-2xl p-8 border border-green-500/30 relative overflow-hidden">
              <motion.div
                className="absolute inset-0 bg-gradient-to-r from-green-500/10 to-transparent"
                animate={{
                  x: ['-100%', '100%'],
                }}
                transition={{
                  duration: 8,
                  repeat: Infinity,
                  ease: "linear"
                }}
              />
              <div className="relative flex items-center mb-6">
                <div className="p-3 rounded-lg bg-green-500/20 border border-green-500/30 mr-4">
                  <ShieldCheckIcon className="h-8 w-8 text-green-400" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white">Speedr AI</h3>
                  <p className="text-green-400 text-sm font-medium">100% Offline</p>
                </div>
              </div>
              <div className="space-y-3">
                <div className="flex items-start">
                  <motion.div
                    className="w-6 h-6 rounded-full bg-green-500 mt-1 mr-3 flex-shrink-0 flex items-center justify-center"
                    animate={{ scale: [1, 1.2, 1] }}
                    transition={{ duration: 2, repeat: Infinity }}
                  >
                    <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8.5 8.5a.75.75 0 01-1.052-.143l-8.5-8.5a.75.75 0 011.052-1.052L8 12.293V4.75a.75.75 0 011.5 0v7.543l7.654-7.654a.75.75 0 011.05.143z" clipRule="evenodd" />
                    </svg>
                  </motion.div>
                  <p className="text-green-300 font-medium">Works completely offline</p>
                </div>
                <div className="flex items-start">
                  <motion.div
                    className="w-6 h-6 rounded-full bg-green-500 mt-1 mr-3 flex-shrink-0 flex items-center justify-center"
                    animate={{ scale: [1, 1.2, 1] }}
                    transition={{ duration: 2, repeat: Infinity, delay: 0.2 }}
                  >
                    <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8.5 8.5a.75.75 0 01-1.052-.143l-8.5-8.5a.75.75 0 011.052-1.052L8 12.293V4.75a.75.75 0 011.5 0v7.543l7.654-7.654a.75.75 0 011.05.143z" clipRule="evenodd" />
                    </svg>
                  </motion.div>
                  <p className="text-green-300 font-medium">Your data never leaves your device</p>
                </div>
                <div className="flex items-start">
                  <motion.div
                    className="w-6 h-6 rounded-full bg-green-500 mt-1 mr-3 flex-shrink-0 flex items-center justify-center"
                    animate={{ scale: [1, 1.2, 1] }}
                    transition={{ duration: 2, repeat: Infinity, delay: 0.4 }}
                  >
                    <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8.5 8.5a.75.75 0 01-1.052-.143l-8.5-8.5a.75.75 0 011.052-1.052L8 12.293V4.75a.75.75 0 011.5 0v7.543l7.654-7.654a.75.75 0 011.05.143z" clipRule="evenodd" />
                    </svg>
                  </motion.div>
                  <p className="text-green-300 font-medium">100% private & secure</p>
                </div>
                <div className="flex items-start">
                  <motion.div
                    className="w-6 h-6 rounded-full bg-green-500 mt-1 mr-3 flex-shrink-0 flex items-center justify-center"
                    animate={{ scale: [1, 1.2, 1] }}
                    transition={{ duration: 2, repeat: Infinity, delay: 0.6 }}
                  >
                    <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8.5 8.5a.75.75 0 01-1.052-.143l-8.5-8.5a.75.75 0 011.052-1.052L8 12.293V4.75a.75.75 0 011.5 0v7.543l7.654-7.654a.75.75 0 011.05.143z" clipRule="evenodd" />
                    </svg>
                  </motion.div>
                  <p className="text-green-300 font-medium">Instant response, no latency</p>
                </div>
                <div className="flex items-start">
                  <motion.div
                    className="w-6 h-6 rounded-full bg-green-500 mt-1 mr-3 flex-shrink-0 flex items-center justify-center"
                    animate={{ scale: [1, 1.2, 1] }}
                    transition={{ duration: 2, repeat: Infinity, delay: 0.8 }}
                  >
                    <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8.5 8.5a.75.75 0 01-1.052-.143l-8.5-8.5a.75.75 0 011.052-1.052L8 12.293V4.75a.75.75 0 011.5 0v7.543l7.654-7.654a.75.75 0 011.05.143z" clipRule="evenodd" />
                    </svg>
                  </motion.div>
                  <p className="text-green-300 font-medium">One-time purchase, no subscriptions</p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Unique Selling Points */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          viewport={{ once: true }}
          className="grid grid-cols-1 md:grid-cols-3 gap-8"
        >
          <div className="text-center">
            <motion.div
              className="mx-auto p-4 rounded-2xl bg-gradient-to-br from-green-500/20 to-primary-500/20 border border-green-500/30 mb-4"
              whileHover={{ scale: 1.05, rotate: 2 }}
              transition={{ duration: 0.2 }}
            >
              <LockClosedIcon className="h-8 w-8 text-green-400" />
            </motion.div>
            <h3 className="text-xl font-bold text-white mb-2">Absolute Privacy</h3>
            <p className="text-primary-200">Your data never leaves your device. No cloud storage, no data mining, no privacy breaches.</p>
          </div>

          <div className="text-center">
            <motion.div
              className="mx-auto p-4 rounded-2xl bg-gradient-to-br from-green-500/20 to-primary-500/20 border border-green-500/30 mb-4"
              whileHover={{ scale: 1.05, rotate: -2 }}
              transition={{ duration: 0.2 }}
            >
              <CpuChipIcon className="h-8 w-8 text-green-400" />
            </motion.div>
            <h3 className="text-xl font-bold text-white mb-2">Local Processing</h3>
            <p className="text-primary-200">Advanced AI models run entirely on your device. No internet required, no server dependencies.</p>
          </div>

          <div className="text-center">
            <motion.div
              className="mx-auto p-4 rounded-2xl bg-gradient-to-br from-green-500/20 to-primary-500/20 border border-green-500/30 mb-4"
              whileHover={{ scale: 1.05, rotate: 2 }}
              transition={{ duration: 0.2 }}
            >
              <DevicePhoneMobileIcon className="h-8 w-8 text-green-400" />
            </motion.div>
            <h3 className="text-xl font-bold text-white mb-2">Always Available</h3>
            <p className="text-primary-200">Works anywhere, anytime. Perfect for remote areas, travel, or when internet is unavailable.</p>
          </div>
        </motion.div>

        {/* Call to Action */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.8 }}
          viewport={{ once: true }}
          className="mt-16 text-center"
        >
          <div className="bg-gradient-to-r from-green-600 to-primary-600 rounded-2xl p-8 border border-green-500/30">
            <h3 className="text-2xl font-bold text-white mb-4">
              Experience True Offline AI
            </h3>
            <p className="text-green-100 mb-6 max-w-2xl mx-auto">
              Join the revolution in AI technology. No internet required, no subscriptions, no compromises on privacy.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.button
                className="px-8 py-3 bg-dark-900 text-green-400 font-semibold rounded-lg hover:bg-dark-800 transition-colors border border-green-500/30"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Try AI Chat Offline
              </motion.button>
              <motion.button
                className="px-8 py-3 bg-dark-900 text-green-400 font-semibold rounded-lg hover:bg-dark-800 transition-colors border border-green-500/30"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Try AI Doctor Offline
              </motion.button>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default UniqueFeature;
